<script lang="ts">
  import { onMount } from 'svelte';
  import { setupProjectAnimations, animateValue } from '$lib/utils/animations';
  import VideoPlayer from '$lib/components/VideoPlayer.svelte';
  import ProjectNav from '$lib/components/ProjectNav.svelte';
  import Footer from '$lib/components/Footer.svelte';
  import FeatureCell from '$lib/components/FeatureCell.svelte';
  import IndustryTag from '$lib/components/IndustryTag.svelte';
  import ProjectHeroSplit from '$lib/components/ProjectHeroSplit.svelte';
  import ProjectHeroFullWidth from '$lib/components/ProjectHeroFullWidth.svelte';
  import FullWidthImage from '$lib/components/FullWidthImage.svelte';
  import Quote from '$lib/components/Quote.svelte';
  
  let scrollY = 0;
  let translateY = 0;
  
  // Make the parallax effect reactive
  $: translateY = scrollY * 0.5;

  let titleContent: HTMLElement;
  let viewsCount = 0;
  let languagesCount = 0;
  let yearsCount = 0;

  onMount(() => {
    // Setup number animations
    const metricsObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animateValue(0, 6, 2000, (val) => viewsCount = val);
          animateValue(0, 3, 1000, (val) => languagesCount = val);
          animateValue(0, 2, 1500, (val) => yearsCount = val);
          metricsObserver.disconnect();
        }
      });
    });

    const metricsSection = document.querySelector('.metrics-section');
    if (metricsSection) {
      metricsObserver.observe(metricsSection);
    }

    // Setup scroll animations
    setupProjectAnimations();

    // Cleanup function
    return () => {
      metricsObserver.disconnect();
    };
  });
</script>

<svelte:window bind:scrollY />

<div class="min-h-screen bg-white">
  <ProjectHeroFullWidth
    title="Blanq"
    client="Blanq Inc"
    industry="Fintech"
    product="Crypto wallet and physical card with distributed-key technology"
    heroMedia={{
      type: 'image',
      src: "https://www.nand.io/_next/image?url=/_next/static/media/blanq.a7178036.jpg&w=3840&q=80"
    }}
    metrics={[
      { value: 6, label: "Months Development" },
      { value: 3, label: "Platforms" },
      { value: 2, label: "Security Splits" }
    ]}
    roles={[
      "Strategy",
      "Visual Identity",
      "UX/UI Design"
    ]}
  />

  <div class="container mx-auto px-8">
    <div class="space-y-2 mb-12 animate-on-view border-t border-b border-black/10 py-6" data-delay="1">
      <p class="large-text text-black/90">
        Materializing a start-up's vision into a sophisticated, user-friendly product.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
      <div class="bg-gray-50 p-8 rounded-lg relative">
        <div class="relative z-10">
          <h2 class="text-2xl font-light mb-4">The Challenge</h2>
          <p class="mb-8 text-gray-700">
            Transforming a fintech start-up's vision into a tangible product by designing and launching a minimum viable product within six months.
          </p>
        </div>
      </div>

      <div class="bg-gray-50 p-8 rounded-lg relative">
        <div class="relative z-10">
          <h2 class="text-2xl font-light mb-4">The Solution</h2>
          <p class="mb-8 text-gray-700">
            We created a crypto wallet mobile application, a physical banking card, and a website to attract early adopter buyers and validate the product concept. Appstore ready.
          </p>
        </div>
      </div>
    </div>
  </div>

  <!-- Video Section 1 -->
  <div class="w-full mt-16 mb-16">
    <div class="w-full">
      <VideoPlayer
        src="https://vz-6b226b53-3c8.b-cdn.net/205dff08-fa0d-4cb3-aa08-a8b441c4d1e8/playlist.m3u8"
        autoplay={true}
        loop={true}
        controls={false}
        muted={true}
        className="w-full h-full object-cover"
      />
    </div>
  </div>

  <div class="container mx-auto px-8">
    <!-- Text Section -->
    <div class="mb-16 animate-on-view">
      <div class="grid grid-cols-12 gap-4">
        <p class="text-black/90 leading-relaxed col-start-2 col-span-10">
          The Blanq crypto wallet tackles two of the most common problems associated with non-custodial wallets: increased responsibility placed on users to safeguard their passwords and the technical complexity of setting up and recovering passwords in case of loss or misplacement.
        </p>
      </div>
    </div>
  </div>

  <!-- Video Section 2 -->
  <div class="w-full mt-16 mb-16">
    <div class="w-full">
      <VideoPlayer
        src="https://vz-6b226b53-3c8.b-cdn.net/0696d5d1-4415-4dc6-b046-b2626e106951/playlist.m3u8"
        autoplay={true}
        loop={true}
        controls={false}
        muted={true}
        className="w-full h-full object-cover"
      />
    </div>
  </div>

  <div class="container mx-auto px-8">
    <!-- Text Section -->
    <div class="mb-16 animate-on-view">
      <div class="grid grid-cols-12 gap-4">
        <p class="text-black/90 leading-relaxed col-start-2 col-span-10">
          In Blanq's pioneering effort to enhance security, we designed a unique physical card that holds one split of the cryptographic secret, marking the product as the first-ever distributed-key hardware wallet. The design features materials closely tied to security: the logo printed in optically variable ink, commonly found on bank notes, and a translucent back that makes the antenna visible and highlights the chip.
        </p>
      </div>
    </div>
  </div>

  <!-- Full Width Image Section -->
  <FullWidthImage
    src="https://www.nand.io/_next/image?url=/_next/static/media/card-back-front.28169ceb.jpg&w=3840&q=80"
    alt="Blanq physical card design"
    caption="The physical card design featuring optically variable ink and a translucent back revealing the antenna and chip."
  />

  <!-- Key Features Section -->
  <div class="container mx-auto px-8">
    <h2 class="text-2xl font-light mt-8 mb-8">Key Features</h2>
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <FeatureCell
        icon="shield"
        title="Distributed Security"
        description="Split cryptographic secrets across hardware components"
      />
      
      <FeatureCell
        icon="smartphone"
        title="Mobile App"
        description="User-friendly interface for iOS and Android"
      />

      <FeatureCell
        icon="credit-card"
        title="Physical Card"
        description="High-security card with optically variable ink"
      />

      <FeatureCell
        icon="refresh-ccw"
        title="Easy Recovery"
        description="Simplified password recovery process"
      />
    </div>
  </div>

  <!-- Additional Feature Cell -->
  <Quote 
    text="Your secret is nowhere — it's Blanq. The first-ever distributed-key hardware wallet, setting new standards in crypto security."
  />

  <!-- Image Gallery -->
  <section class="animate-on-view">
    <FullWidthImage
      src="https://www.nand.io/_next/image?url=/_next/static/media/carrier.4ffbf839.png&w=3840&q=80"
      alt="Blanq physical card design"
      caption="Blanq physical card design"
    />
  </section>

  <!-- Project Navigation -->
  <ProjectNav 
    prevProject={{ title: "Made to Measure", url: "/projects/made-to-measure" }}
    nextProject={{ title: "FactSnap", url: "/projects/fact-snap" }}
  />

  <Footer />
</div>
